import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { XCircle, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCancelBooking } from "@/hooks/useBookings";
import type { CancelBookingRequest, BookingWithOptionsResponse } from "@/types/activities";

interface CancelBookingModalProps {
  open: boolean;
  onClose: () => void;
  booking: BookingWithOptionsResponse;
  onSuccess?: () => void;
}

interface FormData extends CancelBookingRequest {}

export default function CancelBookingModal({
  open,
  onClose,
  booking,
  onSuccess
}: CancelBookingModalProps) {
  const { toast } = useToast();
  const cancelBooking = useCancelBooking();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      cancellationReason: ""
    }
  });

  const onSubmit = async (data: FormData) => {
    try {
      await cancelBooking.mutateAsync({
        bookingId: booking.id,
        data
      });

      toast({
        title: "Booking cancelled",
        description: "The booking has been cancelled successfully. All stakeholders will be notified.",
        duration: 5000
      });

      onSuccess?.();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel booking. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const getImpactWarning = () => {
    switch (booking.status) {
      case "Confirmed":
        return "This will cancel a confirmed booking. The vendor and organization will need to be notified immediately.";
      case "ApprovedAwaitingVendorBooking":
        return "This will cancel an approved booking before vendor confirmation. The organization will be notified.";
      case "PendingOrganizationApproval":
        return "This will cancel the booking during the approval process. HR will be notified.";
      case "VendorNegotiating":
        return "This will cancel ongoing vendor negotiations. All contacted vendors will be notified.";
      default:
        return "This will cancel the booking request. All stakeholders will be notified.";
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <XCircle className="h-5 w-5" />
            Cancel Booking
          </DialogTitle>
          <DialogDescription>
            This action will permanently cancel the booking request. Please provide a reason for the cancellation.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Booking Summary */}
          <div className="p-4 bg-muted/30 rounded-lg">
            <h3 className="font-medium mb-2">Booking to Cancel</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Organization:</span>
                <p className="font-medium">{booking.organizationName}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Activity:</span>
                <p className="font-medium">{booking.activityName}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Participants:</span>
                <p className="font-medium">{booking.estimatedParticipantsCount}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Preferred Date:</span>
                <p className="font-medium">
                  {booking.preferredStartDate ? new Date(booking.preferredStartDate).toLocaleDateString() : 'TBD'}
                </p>
              </div>
            </div>
          </div>

          {/* Impact Warning */}
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Warning:</strong> {getImpactWarning()}
            </AlertDescription>
          </Alert>

          {/* Cancellation Reason */}
          <div className="space-y-2">
            <Label htmlFor="cancellationReason">Cancellation Reason *</Label>
            <Textarea
              id="cancellationReason"
              {...register("cancellationReason", {
                required: "Cancellation reason is required",
                minLength: {
                  value: 10,
                  message: "Please provide a detailed reason (at least 10 characters)"
                }
              })}
              placeholder="Please provide a detailed reason for cancelling this booking..."
              rows={4}
            />
            {errors.cancellationReason && (
              <p className="text-sm text-destructive">{errors.cancellationReason.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              This reason will be shared with the organization and recorded in the booking history.
            </p>
          </div>

          {/* Additional Information */}
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <h4 className="text-sm font-medium text-orange-800 mb-2">What happens when you cancel:</h4>
            <ul className="text-sm text-orange-700 space-y-1">
              <li>• Booking status will be updated to "Cancelled"</li>
              <li>• Organization will receive cancellation notification</li>
              <li>• All contacted vendors will be notified (if applicable)</li>
              <li>• Booking options will be marked as closed</li>
              {booking.status === "Confirmed" && (
                <li>• <strong>Immediate notification required</strong> - confirmed booking cancellation</li>
              )}
            </ul>
          </div>

          {/* Additional impact for confirmed bookings */}
          {booking.status === "Confirmed" && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Critical:</strong> This is a confirmed booking cancellation. You may need to contact the vendor
                directly about cancellation policies and any potential fees.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Keep Booking
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={cancelBooking.isPending}
              className="flex items-center gap-2"
            >
              {cancelBooking.isPending ? (
                "Cancelling..."
              ) : (
                <>
                  <XCircle className="h-4 w-4" />
                  Cancel Booking
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}